import type { ApplicationDraftSteps, DiscrepancyView } from './application'
import type { RecipientScopeView } from './packages'

import type { EligibilityReason, EligibilityResult, EvidenceLevel, ProgramRuleView, RuleEvaluationOutcome, RuleField } from './program-rules'

export type AppMode = 'demo' | 'production'

export interface HealthResponse {
  status: 'ok'
  version: string
  app_mode: AppMode
  environment: string
}

export interface ReadyResponse {
  status: 'ok' | 'degraded'
  checks: { database: 'ok' | 'error' }
}

export type TexRole = 'reviewer' | 'ops_admin'
export type DealerRole = 'owner' | 'admin' | 'member'
export type DealerStatus = 'pending_review' | 'active' | 'suspended'
export type ParticipantRole = 'borrower_representative' | 'owner_participant'
export type ParticipantStatus = 'invited' | 'verified' | 'revoked' | 'expired'
export type Audience = 'tex' | 'dealer' | 'borrower'

/** GET /api/v1/me */
export interface MeResponse {
  user: { id: string; email: string; name: string | null }
  tex_role: TexRole | null
  dealers: { dealer_id: string; organization_id: string; status: DealerStatus; role: DealerRole }[]
  cases: { case_id: string; participant_id: string; role: ParticipantRole }[]
}

export interface ApiError {
  error: string
  reason?: string
}

/** `POST /me/sessions/revoke-others`, `POST /tex/users/:id/sessions/revoke`. */
export interface SessionsRevokedResponse {
  user_id: string
  sessions_revoked: number
}

export type CaseStage =
  | 'lead'
  | 'application'
  | 'review'
  | 'lender_review'
  | 'offer_review'
  | 'closing'
  | 'funded'
  | 'closed'

export interface MoneyView {
  amount_minor: number
  currency: string
}

export const BUSINESS_ENTITY_TYPES = [
  'c_corp',
  's_corp',
  'b_corp',
  'partnership',
  'sole_proprietorship',
  'llc',
  'nonprofit',
  'municipality',
  'native_american_tribe',
  'cooperative',
  'limited_partnership',
  'llp',
  'lllp',
  'division',
  'trust',
  'professional_corporation',
  'professional_association',
] as const
export type BusinessEntityType = (typeof BUSINESS_ENTITY_TYPES)[number]

export const EQUIPMENT_CONDITIONS = ['new', 'used', 'refurbished'] as const
export type EquipmentCondition = (typeof EQUIPMENT_CONDITIONS)[number]

export const EQUIPMENT_TITLE_KINDS = ['none', 'titled', 'vehicle'] as const
export type EquipmentTitleKind = (typeof EQUIPMENT_TITLE_KINDS)[number]

export const SOFT_COST_KINDS = ['tax', 'shipping', 'installation', 'other'] as const
export type SoftCostKind = (typeof SOFT_COST_KINDS)[number]

export interface BuyerAddressView {
  line1: string
  line2?: string
  city: string
  state: string
  postal_code: string
}

export interface EquipmentItemView {
  id: string
  position: number
  category: string
  make: string | null
  model: string | null
  model_year: number | null
  condition: EquipmentCondition
  serial_number: string | null
  hours_or_miles: number | null
  description: string | null
  quantity: number
  unit_price: MoneyView
  /** quantity × unit_price, computed server-side. */
  line_total: MoneyView
  listing_url: string | null
  title_kind: EquipmentTitleKind | null
  vehicle_class: string | null
  existing_lien_noted: boolean
}

export interface SoftCostView {
  id: string
  position: number
  kind: SoftCostKind
  label: string | null
  amount: MoneyView
}

/** All server-computed (docs/M2-CASE-API.md §4); the client never sends totals. */
export interface CasePricingView {
  equipment_total: MoneyView
  financed_soft_costs: MoneyView
  discount: MoneyView
  invoice_total: MoneyView
  cash_contribution: MoneyView
  requested_principal: MoneyView
  reconciliation_reason: string | null
  trade_in_noted: boolean
}

export interface CaseDeliveryView {
  expected_on: string | null
  pre_delivery_payment_requested: boolean
}

export interface DealerBrandView {
  dealer_id: string
  display_name: string
  logo_key: string | null
  accent_color: string | null
}

export interface DealerAddressView {
  line1: string
  line2?: string
  city: string
  state: string
  postal_code: string
  country: 'US'
}

export interface DealerContactView {
  name: string
  title?: string
  email: string
  phone?: string
}

/** GET /api/v1/dealers/:slug/public — anonymous; only `active` dealers resolve. */
export interface DealerPublicView {
  slug: string
  display_name: string
  logo_key: string | null
  accent_color: string | null
}

/** What a dealer's own members see of their dealer record. */
export interface DealerSelfView {
  audience: 'dealer'
  id: string
  organization_id: string
  slug: string
  legal_name: string
  trading_name: string | null
  status: DealerStatus
  website: string | null
  address: DealerAddressView | null
  contacts: DealerContactView[]
  equipment_categories: string[]
  brand: { display_name: string; logo_key: string | null; accent_color: string | null }
  suspended_reason: string | null
  created_at: string
  updated_at: string
}

/** Tex staff additionally see the review trail. */
export interface DealerTexView extends Omit<DealerSelfView, 'audience'> {
  audience: 'tex'
  reviewer_notes: string | null
  verification_method: string | null
  verified_at: string | null
  verified_by: string | null
  suspended_at: string | null
  member_count: number
  case_count: number
}

export type DealerView = DealerSelfView | DealerTexView

export interface DealerListItem {
  id: string
  slug: string
  legal_name: string
  trading_name: string | null
  status: DealerStatus
  member_count: number
  case_count: number
  created_at: string
}

export interface DealerMemberView {
  id: string
  user_id: string
  email: string
  name: string | null
  role: DealerRole
  joined_at: string
}

export interface DealerInvitationView {
  id: string
  email: string
  role: DealerRole
  status: string
  expires_at: string
  invited_by: string
  created_at: string
}

export interface DealerTeamResponse {
  members: DealerMemberView[]
  /** Only present for actors allowed `dealer.manage_team`. */
  invitations?: DealerInvitationView[]
}

export type DealerReviewAction = 'activate' | 'suspend' | 'reinstate'

/** Fields every audience may see. */
export interface CaseViewBase {
  id: string
  reference: string
  stage: CaseStage
  stage_changed_at: string
  transaction_type: 'equipment_purchase' | 'other'
  requested_amount: MoneyView
  pricing: CasePricingView
  soft_costs: SoftCostView[]
  delivery: CaseDeliveryView
  equipment: EquipmentItemView[]
  /** Optimistic-concurrency token; send back as `expected_version` on writes. */
  version: number
  created_at: string
  updated_at: string
}

/** Dealer-supplied lead data about the buyer; borrower-editable later, never borrower-private. */
export interface BuyerLeadView {
  buyer_business_name: string
  buyer_contact_name: string
  buyer_contact_email: string
  buyer_contact_phone: string | null
  buyer_state: string | null
  equipment_use_state: string | null
  buyer_entity_type: BusinessEntityType | null
  buyer_address: BuyerAddressView | null
}

/** Who has been given access to a case. The share-link token itself is never in a view. */
export interface CaseParticipantView {
  id: string
  role: ParticipantRole
  status: ParticipantStatus
  email: string
  display_name: string | null
  invited_at: string
  invite_expires_at: string | null
  verified_at: string | null
  revoked_at: string | null
  revoked_reason: string | null
}

/** POST /cases/:id/invitations and .../resend. `link` carries the raw token and is returned exactly once. */
export interface InvitationIssuedResponse {
  participant: CaseParticipantView
  link: string
}

/** POST /invitations/exchange */
export interface InvitationExchangeResponse {
  case_id: string
}

/** GET /me/cases — cases the signed-in borrower is a verified participant of. */
export interface BorrowerCaseListItem {
  case_id: string
  participant_id: string
  role: ParticipantRole
  reference: string
  stage: CaseStage
  stage_changed_at: string
  buyer_business_name: string
  dealer: DealerBrandView
  requested_principal: MoneyView
  equipment_summary: string
  application_submitted: boolean
  created_at: string
}

export interface BorrowerCaseListResponse {
  cases: BorrowerCaseListItem[]
}

/** GET /outbox/emails — demo-mode preview of suppressed outgoing email (Tex staff only). */
export interface OutboxEmailView {
  id: string
  kind: string
  to: string
  subject: string
  text: string
  status: string
  created_at: string
}

export interface OutboxEmailsResponse {
  emails: OutboxEmailView[]
}

/** What a dealer sees: their own lead data + progress. Never the borrower's answers. */
export interface DealerCaseView extends CaseViewBase, BuyerLeadView {
  audience: 'dealer'
  /** The dealer's own id, so a multi-dealer user can tell which membership governs this case. */
  dealer_id: string
  dealer_note: string | null
  close_reason: string | null
  application_submitted: boolean
  participants: CaseParticipantView[]
}

export interface CaseListItem {
  id: string
  reference: string
  stage: CaseStage
  stage_changed_at: string
  buyer_business_name: string
  buyer_contact_name: string
  requested_principal: MoneyView
  equipment_summary: string
  /** Tex list only; dealers see their own cases. */
  dealer_id?: string
  dealer_legal_name?: string
  created_at: string
}

export interface CaseListResponse {
  cases: CaseListItem[]
  next_cursor: string | null
}

/** What the borrower sees: the dealer's brand, the request and their own application. */
export interface BorrowerCaseView extends CaseViewBase {
  audience: 'borrower'
  dealer: DealerBrandView
  buyer_business_name: string
  buyer_contact_name: string
  application: {
    revision_number: number
    submitted_at: string
    payload: ApplicationDraftSteps
  } | null
}

/** Tex sees everything, including review state and the submitted application. */
export interface TexCaseView extends CaseViewBase, BuyerLeadView {
  audience: 'tex'
  dealer_id: string
  dealer_legal_name: string
  dealer_status: DealerStatus
  created_by_user_id: string | null
  dealer_note: string | null
  close_reason: string | null
  close_note: string | null
  referral_category: string | null
  referred_to: string | null
  assigned_reviewer_id: string | null
  specialist_review_required: boolean
  selected_offer_id: string | null
  participants: CaseParticipantView[]
  application: {
    id: string
    revision_number: number
    submitted_at: string
    payload_sha256: string
    schema_version: string
    payload: ApplicationDraftSteps
    /** Frozen at submit; reconciliation is the reviewer's (PR 10). */
    discrepancies: DiscrepancyView[]
  } | null
}

export type CaseView = DealerCaseView | BorrowerCaseView | TexCaseView

/** POST /cases. `invitation` is present only when the request carried `invite: { send: true }`. */
export interface CaseCreatedResponse {
  case: CaseView
  invitation?: InvitationIssuedResponse
}

// ---------------------------------------------------------------------------
// Documents (M2 PR 7). Storage keys never leave the API; access is by short-lived URL only.
// ---------------------------------------------------------------------------

export type DocumentClassification = 'borrower_private' | 'transaction' | 'partner_supplied' | 'package_export' | 'dealer_onboarding'
export type DocumentScanStatus = 'pending' | 'clean' | 'infected' | 'failed'
export type DocumentUploadStatus = 'pending_upload' | 'stored' | 'rejected'
export type DocumentMimeType =
  | 'application/pdf'
  | 'image/png'
  | 'image/jpeg'
  | 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
  | 'text/csv'

/**
 * `available` ⇔ stored, scan clean, not deleted: the only state that yields an access URL.
 * Everything else is quarantined from preview, packaging and sending (AC10).
 */
export type DocumentAvailability = 'awaiting_upload' | 'scanning' | 'available' | 'quarantined' | 'rejected' | 'deleted'

export interface DocumentViewBase {
  id: string
  case_id: string
  document_type: string
  classification: DocumentClassification
  original_filename: string
  declared_mime_type: DocumentMimeType
  detected_mime_type: string | null
  size_bytes: number
  sha256: string | null
  upload_status: DocumentUploadStatus
  scan_status: DocumentScanStatus
  scanned_at: string | null
  availability: DocumentAvailability
  version: number
  supersedes_document_id: string | null
  superseded_at: string | null
  /** Whether the caller may delete this row (uploader or Tex, and no legal hold). */
  can_delete: boolean
  created_at: string
  updated_at: string
}

export interface DealerDocumentView extends DocumentViewBase {
  audience: 'dealer'
  uploaded_by_me: boolean
}

export interface BorrowerDocumentView extends DocumentViewBase {
  audience: 'borrower'
  uploaded_by_me: boolean
}

export interface TexDocumentView extends DocumentViewBase {
  audience: 'tex'
  uploaded_by_user_id: string | null
  uploaded_by_participant_id: string | null
  scan_engine: string | null
  scan_signature: string | null
  legal_hold: boolean
  deleted_at: string | null
}

export type DocumentView = DealerDocumentView | BorrowerDocumentView | TexDocumentView

export interface DocumentListResponse {
  documents: DocumentView[]
}

export interface UploadAuthorizationRequest {
  document_type: string
  classification: DocumentClassification
  declared_mime_type: DocumentMimeType
  original_filename: string
  size_bytes: number
  /** Optional client-side hash; the server recomputes and records its own on `complete`. */
  sha256?: string
}

export interface UploadAuthorizationResponse {
  document: DocumentView
  upload: {
    method: 'PUT'
    url: string
    expires_at: string
    /** Headers the browser must send with the PUT; nothing else is signed. */
    headers: Record<string, string>
  }
}

export interface DocumentAccessResponse {
  document: DocumentView
  access: {
    method: 'GET'
    url: string
    expires_at: string
  }
}

// ── partners, programs, jurisdictions, eligibility (Tex-internal; never in dealer/borrower views) ──

export type PartnerStatus = 'prospective' | 'active' | 'suspended' | 'terminated'
export type SubmissionMethod = 'manual_portal' | 'mock' | 'api'
export type ProgramStatus = 'draft' | 'test_only' | 'active' | 'retired'
export type JurisdictionStatus = 'approved' | 'pending' | 'blocked'
export type PartnerStatusCommand = 'activate' | 'suspend' | 'terminate'
export type ProgramStatusCommand = 'mark_test_only' | 'activate' | 'retire'

export const RESPONSIBLE_PARTIES = ['borrower', 'dealer', 'tex', 'partner_direct'] as const
export type ResponsibleParty = (typeof RESPONSIBLE_PARTIES)[number]

export interface PartnerView {
  id: string
  code: string
  legal_name: string
  display_name: string
  status: PartnerStatus
  status_reason: string | null
  is_synthetic: boolean
  submission_method: SubmissionMethod
  submission_instructions: string | null
  /** Recipient whitelist for packages; `null` until an ops admin records what the partner agreement allows. */
  authorized_recipient_scope: RecipientScopeView | null
  agreement_reference: string | null
  agreement_effective_from: string | null
  agreement_effective_to: string | null
  access_verified_at: string | null
  access_verified_by: string | null
  program_count: number
  created_at: string
  updated_at: string
}

export interface PartnerListResponse {
  partners: PartnerView[]
}

export interface DocumentRequirementView {
  document_type: string
  responsible_party: ResponsibleParty
  required: boolean
  description?: string
}

export interface ProgramVersionView {
  id: string
  partner_id: string
  program_code: string
  version: number
  status: ProgramStatus
  title: string
  rules: ProgramRuleView[]
  unknown_policy: string
  document_requirements: DocumentRequirementView[]
  effective_from: string | null
  effective_to: string | null
  criteria_verified_at: string | null
  criteria_verified_by: string | null
  created_by: string | null
  created_at: string
  updated_at: string
}

export interface ProgramVersionListResponse {
  programs: ProgramVersionView[]
}

export interface JurisdictionView {
  state: string
  status: JurisdictionStatus
  basis: string | null
  effective_from: string | null
  effective_to: string | null
  decided_by: string | null
  updated_at: string
}

export interface JurisdictionListResponse {
  jurisdictions: JurisdictionView[]
}

export interface DisclosureJurisdictionView {
  state: string
  law_reference: string
  threshold_amount: MoneyView | null
  broker_duty_note: string | null
  verified_at: string | null
  verified_by: string | null
  updated_at: string
}

/** One fact as the evaluator saw it: known with an evidence level, or `unknown`. */
export type CaseFactView =
  | { status: 'known'; value: string | number | string[]; evidence: EvidenceLevel }
  | { status: 'unknown' }

export interface RuleEvaluationView {
  field: RuleField | string
  operator: string
  expected: unknown
  actual: string | number | string[] | null
  evidence: EvidenceLevel | 'unknown'
  result: RuleEvaluationOutcome
}

export interface JurisdictionCheckView {
  field: 'borrower.state' | 'equipment.use_state'
  state: string | null
  status: JurisdictionStatus | 'missing' | 'unknown'
}

export interface EligibilityEvaluationView {
  id: string
  case_id: string
  revision_id: string | null
  program_version_id: string
  program_code: string
  program_version: number
  program_title: string
  partner_id: string
  partner_display_name: string
  result: EligibilityResult
  reasons: EligibilityReason[]
  rules_snapshot: ProgramRuleView[]
  facts: Record<RuleField, CaseFactView>
  jurisdiction_checks: JurisdictionCheckView[]
  rule_evaluations: RuleEvaluationView[]
  unknown_fields: string[]
  evaluated_by_user_id: string | null
  evaluated_at: string
}

export interface EligibilityEvaluationListResponse {
  evaluations: EligibilityEvaluationView[]
}
